import { repositoryHelperFactory } from "$root/api/repository";
import { noRefetchDefaults } from "$root/third-party-integrations/react-query/hooks";
import { deleteConversation, listConversations, renameConversation, setConversationFavourite } from "./serverChatStore";

/**
 * The conversation COLLECTION as server state (MDOT-24442): the History list, plus the three
 * metadata writes that reorder or remove a row. Wraps `serverChatStore`'s sphere-mcp calls in the
 * house repository shape so each write invalidates the list itself, instead of every call site
 * awaiting a `refetch` threaded down as a prop from whoever happens to own the query.
 *
 * The TRANSCRIPT half of `serverChatStore` (`getConversation` / `upsertConversation`) deliberately
 * stays out of here, and out of any repository whose keys a write can reach. Its read feeds
 * `setMessages` on the live `useChat` instance, so an invalidation landing on it mid-turn would
 * overwrite the streaming transcript with the last persisted snapshot — the hazard
 * `CHAT_OWNED_QUERY_KEYS` (botWriteInvalidation.ts) exists to prevent. It is driven imperatively by
 * the chat lifecycle, not by cache staleness.
 */

/** Query-key segment for the list, and the repository's only `listKey` — so a write refreshes it. */
const listKey = "list";

/**
 * Root query-key segment. Exported because `botWriteInvalidation` has to name it to keep the list
 * chat-owned: importing the constant is what stops the two drifting apart on a rename.
 */
export const sphereAiConversationsRepositoryName = "sphereAiConversations";

const { newQuery, newAction, invalidateLists } = repositoryHelperFactory(sphereAiConversationsRepositoryName, {
	listKeys: [listKey],
});

export const sphereAiConversationsRepository = {
	/**
	 * Conversation metadata for the History tab, in the server's order (favourites first, then newest
	 * activity). Nothing pre-sorts it: `groupHistory` re-sorts both groups on `favouritedAt`/activity
	 * for display, so a second ordering here would only be a second answer to the same question.
	 *
	 * `noRefetchDefaults` preserves the pre-repository behaviour — fetch on mount, then only when one
	 * of the writes below invalidates the list; never on window focus or reconnect.
	 */
	conversations: newQuery(listKey, listConversations, noRefetchDefaults),

	deleteConversation: newAction(async (uuid: string) => {
		await deleteConversation(uuid);
		invalidateLists();
	}),

	renameConversation: newAction(async (uuid: string, name: string) => {
		await renameConversation(uuid, name);
		invalidateLists();
	}),

	setConversationFavourite: newAction(async (uuid: string, favourite: boolean) => {
		await setConversationFavourite(uuid, favourite);
		invalidateLists();
	}),

	/**
	 * Refresh the list after a finished turn was persisted with `persistConversation` — the one write
	 * that lives outside this repository (see the note above), so it cannot invalidate on its own.
	 * That call is what makes a brand-new conversation appear in History at all.
	 */
	invalidateList: invalidateLists,
};
